/**
 * Utilities for keeping user-created playlist names from colliding with
 *  - the four reserved Gaia system surfaces ("Currently Watching",
 *    "Watch Later", "Favorites", "Watch History"), and
 *  - the user's own existing custom playlists.
 *
 * The backend today does not enforce either invariant — system playlists
 * are discriminated by `playlistType`, not by `playlistName`, so a user
 * playlist named "Watch Later" with `playlistType: 'user'` is accepted.
 * The collision is purely visual, but it confuses the library UI.
 *
 * Resolution: when the requested name (case-insensitively, trim-normalized)
 * collides with a reserved or existing name, we silently auto-suffix to
 * the first non-colliding form: `"Watch Later" → "Watch Later 2"`.
 *
 * Pair with `useReservedSystemPlaylistNames` (which reads the four reserved
 * names from staticText so they're locale-aware) to assemble the
 * `reservedNames` argument.
 *
 * The duplicate-playlist flow uses a different name pattern (with " - Copy"
 * token) — see `playlist-suffix-walk.ts` for that helper.
 */

export interface SystemPlaylistReservedKey {
  readonly key: string
  readonly fallback: string
}

export const SYSTEM_PLAYLIST_RESERVED_KEYS: ReadonlyArray<SystemPlaylistReservedKey> = [
  { key: 'currentlyWatching', fallback: 'Currently Watching' },
  { key: 'watchLater', fallback: 'Watch Later' },
  { key: 'favorites', fallback: 'Favorites' },
  { key: 'watchHistory', fallback: 'Watch History' },
]

const norm = (s: string): string => s.trim().toLowerCase()

/**
 * Resolves a non-colliding playlist name.
 *
 * If `requested` (case-insensitive, trimmed) matches any reserved system
 * name OR any name in `existingNames`, walks "<requested> 2",
 * "<requested> 3", … and returns the first non-colliding form. Returns
 * `requested` (trimmed) unchanged when no collision.
 *
 * The user's typed casing is preserved — e.g. "WATCH LATER" becomes
 * "WATCH LATER 2", not "Watch Later 2".
 *
 * @param requested      the name the user typed (trimming handled here)
 * @param reservedNames  lower-cased, trimmed Set of reserved system names —
 *                       produced by `useReservedSystemPlaylistNames`
 * @param existingNames  raw names of the user's existing playlists; will
 *                       be normalized internally. Pass an empty array if
 *                       the list isn't available yet.
 */
export function resolveNonCollidingPlaylistName (
  requested: string,
  reservedNames: ReadonlySet<string>,
  existingNames: ReadonlyArray<string>,
): string {
  const trimmed = requested.trim()
  if (!trimmed) return trimmed

  const taken = new Set<string>(reservedNames)
  existingNames.forEach((n) => {
    if (typeof n === 'string' && n) taken.add(norm(n))
  })

  if (!taken.has(norm(trimmed))) return trimmed

  // Defensive ceiling — guards an unforeseen pathological case where
  // 998 numbered variants somehow already exist. `Array.from` + `.find`
  // satisfies the project's `no-restricted-syntax` rule (no raw `for`).
  const candidate = Array
    .from({ length: 998 }, (_, i) => `${trimmed} ${i + 2}`)
    .find((c) => !taken.has(norm(c)))
  return candidate ?? `${trimmed} 1000`
}
